import { useContext, useEffect, useMemo, useState } from 'react'
import GeoContext from 'contexts/geoContext'
import { useHotelTrendingDestinations } from 'home/hooks/useHotelTrendingDestinations'
import { getPlaceById } from 'api/search'
import { findByTwoLetterCode } from 'lib/insurance/insuranceCountries'
import { CityTrendingDestination } from './TripLandingPageTrendingDestinations'
import { DEFAULT_FILTER } from 'home/components/TrendingDestinations/TrendingDestinations'

export const useTrendingDestinations = (destinations: Array<CityTrendingDestination>) => {
  const { currentRegionCode } = useContext(GeoContext)
  const [cityTrendingDestinations, setCityTrendingDestinations] = useState<Array<CityTrendingDestination>>([])
  const [isFiltering, setIsFiltering] = useState(false)

  const [displayTrendingDestinations, fetching] = useHotelTrendingDestinations(DEFAULT_FILTER, currentRegionCode)

  useEffect(() => {
    if (!fetching && displayTrendingDestinations.length > 0) {
      setIsFiltering(true)

      const filterCities = async() => {
        try {
          const placePromises = displayTrendingDestinations
            .filter(destination => destination.destinationId)
            .map(async(destination) => {
              try {
                const placeDetails = await getPlaceById(destination.destinationId!)
                if (placeDetails.type === 'city') {
                  const countryName = placeDetails.countryCode ?
                    findByTwoLetterCode(placeDetails.countryCode) || placeDetails.countryCode :
                    undefined

                  return {
                    ...destination,
                    country: countryName,
                  } as CityTrendingDestination
                }
                return null
              } catch(error) {
                console.error(`Failed to get place details for ${destination.destinationId}:`, error)
                return null
              }
            })

          const results = await Promise.all(placePromises)
          const cityDestinations = results.filter((result) => result !== null)

          setCityTrendingDestinations(cityDestinations)
        } catch(error) {
          console.error('Error filtering cities:', error)
        } finally {
          setIsFiltering(false)
        }
      }

      filterCities()
    }
  }, [displayTrendingDestinations, fetching])

  const finalDestinations = useMemo(() => {
    const existingNames = new Set(cityTrendingDestinations.map(dest => dest.name))
    const availableFallbacks = destinations.filter(dest => !existingNames.has(dest.name))
    const targetCount = 11
    const neededCount = Math.max(0, targetCount - cityTrendingDestinations.length)
    const selectedFallbacks = availableFallbacks.slice(0, neededCount)

    return [...cityTrendingDestinations, ...selectedFallbacks]
  }, [cityTrendingDestinations, destinations])

  return {
    finalDestinations,
    fetchingDestinations: fetching || isFiltering
  }
}
